import { pick } from 'es-toolkit';
import React from 'react';
import { Box, FormControl } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import {
  Component,
  CoordinateAxis,
  LoadDefinitionWrapper,
  LoadInsertionMethod,
  LoadWrapper,
  ManualLoad,
  ManualLoadDefinition,
} from '@paragon/contract-library';
import { Direction2D, distanceEquals, Point2D } from '@paragon/geometry-library';
import { Option } from '@paragon/layout-models';

import {
  direction2DToLoadDirection,
  loadDirectionToDirection2D,
  ManualLoadPointsFields,
} from './ManualLoadPointsFields';
import { ManualLoadProjectedCoordinatesFields } from './ManualLoadProjectedCoordinatesFields';

type LoadInsertionMethodTypeOptions = { [key in LoadInsertionMethod]: Option<LoadInsertionMethod> };
const loadInsertionMethodTypeOptions: LoadInsertionMethodTypeOptions = {
  ProjectedCoordinates: {
    name: 'Select Coordinate Axis',
    value: 'ProjectedCoordinates',
  },
  Points: {
    name: 'Select Member',
    value: 'Points',
  },
};

type ManualLoadFieldProps = {
  readonly loadOrLoadDefinition: LoadDefinitionWrapper | LoadWrapper;
  readonly component: Component;
  readonly onChangeLoadOrLoadDefinition: (updatedLoadOrLoadDefinition: LoadDefinitionWrapper | LoadWrapper) => void;
  readonly coordinateIsOnComponent: (definitionAxis: CoordinateAxis, coordinate: number) => boolean;
  readonly submitIfInvalid?: boolean;
  readonly disabled?: boolean;
};

export function ManualLoadFields(props: ManualLoadFieldProps) {
  const {
    loadOrLoadDefinition,
    component,
    disabled,
    onChangeLoadOrLoadDefinition,
    coordinateIsOnComponent,
    submitIfInvalid,
  } = props;

  function updateManualLoadSettingsWithManualLoad(manualLoad: ManualLoad) {
    if (loadOrLoadDefinition.loadInsertionMethod !== 'Points')
      throw new Error('Cannot update manual load - wrong input type');
    onChangeLoadOrLoadDefinition({
      ...loadOrLoadDefinition,
      load: manualLoad,
    });
  }

  function updateManualLoadSettingsWithManualLoadDefinition(manualLoadDefinition: ManualLoadDefinition) {
    if (loadOrLoadDefinition.loadInsertionMethod !== 'ProjectedCoordinates')
      throw new Error('Cannot update manual load - wrong input type');
    onChangeLoadOrLoadDefinition({
      ...loadOrLoadDefinition,
      loadDefinition: manualLoadDefinition,
    });
  }

  function loadInsertionMethodChange(option: Option<LoadInsertionMethod>) {
    if (loadOrLoadDefinition.loadInsertionMethod === option.value) return;

    if (option.value === 'Points') {
      // Changing from ProjectedCoordinates to Points
      if (loadOrLoadDefinition.loadInsertionMethod !== 'ProjectedCoordinates') {
        throw new Error(
          'This should not happen because we already checked that the new load insertion method differs from the current method',
        );
      }

      onChangeLoadOrLoadDefinition({
        load: inferLoadFromLoadDefinition(loadOrLoadDefinition.loadDefinition),
        loadInsertionMethod: option.value,
      });
    } else {
      // Changing from Points to ProjectedCoordinates
      if (loadOrLoadDefinition.loadInsertionMethod !== 'Points') {
        throw new Error(
          'This should not happen because we already checked that the new load insertion method differs from the current method',
        );
      }

      onChangeLoadOrLoadDefinition({
        loadDefinition: inferLoadDefinitionFromLoad(loadOrLoadDefinition.load),
        loadInsertionMethod: option.value,
      });
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <FormControl sx={{ m: 1 }}>
        <PropertySelectField
          property="Load Insertion Method"
          value={loadInsertionMethodTypeOptions[loadOrLoadDefinition.loadInsertionMethod]}
          options={Object.values(loadInsertionMethodTypeOptions)}
          getName={(option) => option.name}
          getValue={(option) => option.value}
          submit={loadInsertionMethodChange}
          disabled={disabled}
        />
      </FormControl>
      {loadOrLoadDefinition.loadInsertionMethod === 'Points' ? (
        <ManualLoadPointsFields
          key={loadOrLoadDefinition.load.guid}
          manualLoad={loadOrLoadDefinition.load}
          component={component}
          onUpdate={updateManualLoadSettingsWithManualLoad}
          coordinateIsOnComponent={coordinateIsOnComponent}
          submitIfInvalid={submitIfInvalid}
          disabled={disabled}
        />
      ) : (
        <ManualLoadProjectedCoordinatesFields
          key={loadOrLoadDefinition.loadDefinition.guid}
          manualLoadDefinition={loadOrLoadDefinition.loadDefinition}
          component={component}
          onUpdate={updateManualLoadSettingsWithManualLoadDefinition}
          coordinateIsOnComponent={coordinateIsOnComponent}
          canSubmitWhenInvalid={submitIfInvalid}
          disabled={disabled}
        />
      )}
    </Box>
  );
}

function inferLoadFromLoadDefinition(loadDefinition: ManualLoadDefinition): ManualLoad {
  const sharedManualLoadProperties: Pick<ManualLoad, 'direction' | 'guid' | 'loadType' | 'memberType' | 'name'> = {
    direction: pick(loadDirectionToDirection2D(loadDefinition.loadDirection), ['x', 'y']),
    guid: loadDefinition.guid,
    loadType: loadDefinition.loadType,
    memberType:
      loadDefinition.memberType === 'LeftEndVertical' || loadDefinition.memberType === 'RightEndVertical'
        ? 'EndVertical'
        : loadDefinition.memberType,
    name: loadDefinition.name,
  };
  if (loadDefinition.distribution === 'Concentrated') {
    return {
      ...sharedManualLoadProperties,
      distribution: loadDefinition.distribution,
      start: getPointForCoordinate(loadDefinition.definitionAxis, loadDefinition.coordinate),
      startMagnitude: loadDefinition.magnitude,
      endMagnitude: loadDefinition.magnitude,
      magnitudeUnit: 'Pounds',
    };
  }

  return {
    ...sharedManualLoadProperties,
    distribution: loadDefinition.distribution,
    start: getPointForCoordinate(loadDefinition.definitionAxis, loadDefinition.startCoordinate),
    end: getPointForCoordinate(loadDefinition.definitionAxis, loadDefinition.endCoordinate),
    startMagnitude: loadDefinition.startMagnitude,
    endMagnitude: loadDefinition.endMagnitude,
    magnitudeUnit: loadDefinition.magnitudeUnit,
  };
}

function getPointForCoordinate(definitionAxis: CoordinateAxis, coordinate: number) {
  return definitionAxis === 'X' ? new Point2D(coordinate, 0) : new Point2D(0, coordinate);
}

function inferLoadDefinitionFromLoad(load: ManualLoad): ManualLoadDefinition {
  const startPoint = Point2D.fromObject(load.start);
  const endPoint = load.distribution === 'Distributed' ? Point2D.fromObject(load.end) : null;

  const sharedManualLoadDefinitionProperties: Pick<
    ManualLoadDefinition,
    'guid' | 'loadDirection' | 'loadType' | 'memberType' | 'name'
  > = {
    guid: load.guid,
    loadDirection: direction2DToLoadDirection(Direction2D.fromObject(load.direction)) ?? 'GlobalDown',
    loadType: load.loadType,
    memberType: 'TopChord',
    name: load.name,
  };
  if (load.distribution === 'Concentrated') {
    return {
      ...sharedManualLoadDefinitionProperties,
      distribution: load.distribution,
      definitionAxis: 'X',
      coordinate: load.start.x,
      magnitude: load.startMagnitude,
    };
  }

  const definitionAxis: CoordinateAxis =
    load.distribution === 'Distributed' &&
    distanceEquals(startPoint.x, endPoint!.x) &&
    !distanceEquals(startPoint.y, endPoint!.y)
      ? 'Y'
      : 'X';

  return {
    ...sharedManualLoadDefinitionProperties,
    distribution: load.distribution,
    definitionAxis,
    startCoordinate: definitionAxis === 'X' ? startPoint.x : startPoint.y,
    endCoordinate: definitionAxis === 'X' ? endPoint!.x : endPoint!.y,
    startMagnitude: load.startMagnitude,
    endMagnitude: load.endMagnitude,
    magnitudeUnit: load.magnitudeUnit,
    isProjected: false,
  };
}
